import { PropTypes } from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { connect, shallowEqual, useSelector } from 'react-redux'
import { filterStationCoordinates, getWGS84Coordinate } from '../../../../../utils/mapUtils/CoordinateUtils'
import OSMSatellite from '../../../../../utils/mapUtils/layers/OSMSatellite'
import SingleStationPoint from '../../../../../utils/mapUtils/layers/SingleStationPoint'
import OlMap from '../../../../../utils/mapUtils/Map'
import { getSiteType } from '../../../../../utils/mapUtils/SiteTypes'
import { execByType } from '../../../../../utils/StationUtils'
import { Card, CardContent } from '@mui/material'
import KML from '../../../../../utils/mapUtils/layers/KML'
import MapStyles from '../../../../../utils/mapUtils/MapStyles'
import { max } from 'lodash'
import { compact, isUndefined } from 'lodash'

const CARTO_MNEMONIQUUE = 'CAR'
const IS_ACTIVE = '1'

const usualFilesFilters = file => file.name?.endsWith('.kml') && file.type === CARTO_MNEMONIQUUE && file.active === IS_ACTIVE

const StationMapDashboardPanel = ({
    station,
    type,
    className,
    noMarkerTooltip,
    fullScreen,
    actionButtons,
    specificFiles = [],
}) => {
    const {
        citiesIndex,
        files,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
        files: store.StationReducer.files,
    }), shallowEqual)

    const getAacFile = useCallback(() => {
        const aacFile = specificFiles.find(f => f.aacFromSandre && usualFilesFilters(f))
        if (aacFile) {
            return KML({
                url: aacFile.url,
                style: MapStyles.FILL_RED,
                headers: true,
                extractStyles: false,
            })
        }
        const kmlAreaFiles = files.filter(f => usualFilesFilters(f))
        const mostRecentFile = (kmlAreaFiles.length > 0) ?
            kmlAreaFiles.reduce((mostRecent, act) => max([act.date, act.updateDate]) > max([mostRecent.date, mostRecent.updateDate]) ? act : mostRecent, kmlAreaFiles[0]) :
            undefined
        return !isUndefined(mostRecentFile) ? KML({
            url: mostRecentFile.url,
            style: MapStyles.FILL_RED,
            headers: true,
            extractStyles: false,
        }) : null
    }, [files, specificFiles])

    const data = useMemo(() => {
        const stationWithCoordinates = filterStationCoordinates(station, citiesIndex)

        const gridSafran = files.find(f => f.type === CARTO_MNEMONIQUUE && f.name.endsWith('GRILLE_SAFRAN.kml'))
        const safranFile = gridSafran ? KML({
            url: gridSafran.url,
            style: MapStyles.FILL_BLUE,
            headers: true,
            extractStyles: false,
        }) : null

        const aacFile = getAacFile()

        const gridWatermass = specificFiles.find(f => f.watermassFromSandre && usualFilesFilters(f))
        const watermassFile = gridWatermass ? KML({
            url: gridWatermass.url,
            style: MapStyles.FILL_BLUE_GREEN,
            headers: true,
            extractStyles: false,
        }) : null

        const gridWatersheds = specificFiles.filter(f => f.watershedFromSandre && usualFilesFilters(f)).filter((f, index) => index < 4)
        const watershedsFiles = gridWatersheds.map(f => KML({
            url: f.url,
            style: MapStyles.FILL_BLUE,
            headers: true,
            extractStyles: false,
        }))

        const gridStationWatershed = specificFiles.find(f => f.stationWatershedFromCoordinates && usualFilesFilters(f))
        const stationWatershedFile = gridStationWatershed ? KML({
            url: gridStationWatershed.url,
            style: MapStyles.FILL_BLUE,
            headers: true,
            extractStyles: false,
        }) : null

        if (stationWithCoordinates) {
            const stationData = SingleStationPoint({
                site: stationWithCoordinates,
                type: execByType(type, {
                    contact: () => getSiteType('CONTACT', 1),
                    quality: () => getSiteType('QUALITOMETER', 1),
                    piezometry: () => getSiteType('PIEZOMETER', 1),
                    pluviometry: () => getSiteType('PLUVIOMETER', 1),
                    hydrometry: () => getSiteType('HYDROMETRIC_STATION', 1),
                    productionUnit: () => getSiteType('UNIT', 1),
                    distributionUnit: () => getSiteType('UNIT', 1),
                    installation: () => {
                        return (() => {
                            switch (station.installationType) {
                                case 0:
                                    return getSiteType('INSTALLATION_BOREHOLE', 1)
                                case 1:
                                    return getSiteType('INSTALLATION_INDUSTRIAL_SITE', 1)
                                case 2:
                                    return getSiteType('INSTALLATION_STEP', 1)
                                case 3:
                                    return getSiteType('INSTALLATION_FLOW_OBSTRUCTION', 1)
                                case 4:
                                    return getSiteType('INSTALLATION_POLLUTED_SOIL', 1)
                                case 5:
                                    return getSiteType('INSTALLATION_FISHING_SPOT', 1)
                                case 6:
                                    return getSiteType('INSTALLATION_CAVITY', 1)
                                case 7:
                                    return getSiteType('INSTALLATION_SAMPLE_WORK', 1)
                                case 8:
                                    return getSiteType('INSTALLATION_CAPTURE', 1)
                                case 9:
                                    return getSiteType('INSTALLATION_LIFTING_STATION', 1)
                                case 11:
                                    return getSiteType('INSTALLATION_TREATMENT', 1)
                                case 12:
                                    return getSiteType('INSTALLATION_TANK', 1)
                                default:
                                    return getSiteType('INSTALLATION', 1)
                            }
                        })()
                    },
                }),
            })
            const center = getWGS84Coordinate(stationWithCoordinates)
            const layersGroup = compact([OSMSatellite(), safranFile, aacFile, watermassFile, ...watershedsFiles, stationWatershedFile, stationData])
            return [layersGroup, {
                size: 300,
                center,
                zoom: 14,
            }]
        }
        const layersGroup = compact([OSMSatellite(), safranFile, aacFile, watermassFile, ...watershedsFiles, stationWatershedFile])
        return [layersGroup, {
            size: 300,
            center: [47.123010186224576, -1.820669934399723],
            zoom: 14,
        }]
    }, [station, citiesIndex, type, files, specificFiles, getAacFile])

    if (data[0].length !== 1) {
        return (
            <Card className={`${className}`}>
                <CardContent style={{ padding: 0 }}>
                    <OlMap
                        noMarkerTooltip={noMarkerTooltip}
                        stationExport={station}
                        mapConf={data[1]}
                        layers={data[0]}
                        fullScreen={fullScreen}
                        actionButtons={actionButtons}
                    />
                </CardContent>
            </Card>
        )
    }
    return null
}

StationMapDashboardPanel.propTypes = {
    station: PropTypes.object, // DtoStation
    type: PropTypes.string,
    citiesIndex: PropTypes.object, // CityDto
    fullScreen: PropTypes.bool,
    actionButtons: PropTypes.arrayOf(PropTypes.element),
    className: PropTypes.string,
    noMarkerTooltip: PropTypes.bool,
    specificFiles: PropTypes.arrayOf(PropTypes.object), // DtoFile
}

const mapStateToProps = store => {
    return {
        citiesIndex: store.CityReducer.citiesIndex,
    }
}

export default connect(mapStateToProps)(StationMapDashboardPanel)